// =============================================================================
// SendDevisOmvModal — Brique B : envoi d'un devis OMV par email
// =============================================================================
// Solène/Etincelle : « Envoyer » sur un devis OMV → choisit un template email
// (ceux du WYSIWYG Design Studio, collection email_templates owner=design_studio,
// SCOPÉS tenant) → aperçu avec variables RÉSOLUES (snapshot devis/dossier via le
// résolveur des PDF, côté backend) → envoi via le sender OMV avec le PDF en PJ.
//
// Réutilise la MÊME source de templates que l'éditeur WYSIWYG
// (GET /api/governance/email-config/templates?owner=design_studio). La résolution
// des variables et la génération du PDF sont faites côté backend (endpoints
// /api/metier/devis/{id}/email-preview et /send-email) — pas de logique dupliquée.
// =============================================================================
import { useState, useEffect, useCallback } from "react";
import { Send, Loader2, Mail } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { getCurrentTenantSlug } from "../../lib/utils";
import { enteteTenant } from "../../utils/enteteTenant";

const API = process.env.REACT_APP_BACKEND_URL || "";

function headers(tenantId) {
  const token = localStorage.getItem("session_token");
  return {
    "Content-Type": "application/json",
    ...enteteTenant(tenantId || getCurrentTenantSlug()),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export default function SendDevisOmvModal({ open, onClose, devis, tenantId, onSent }) {
  const slug = tenantId || getCurrentTenantSlug();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [templateKey, setTemplateKey] = useState("");
  const [recipient, setRecipient] = useState("");
  const [preview, setPreview] = useState(null); // {subject, html, resolved_variables}
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);

  const devisId = devis?.id || devis?._id || "";

  // Charger les templates email du tenant (MÊME source que le WYSIWYG).
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setPreview(null);
    // Sélecteur d'envoi de devis : SEULEMENT les templates de catégorie "devis"
    // (exclut invitation/onboarding/marketing/système → plus de {{ERREUR:invite_link}}).
    fetch(`${API}/api/governance/email-config/templates?owner=design_studio&category=devis`, {
      headers: headers(slug), credentials: "include",
    })
      .then((r) => r.json())
      .then((data) => {
        const list = (data?.templates || []).filter((t) => t.active !== false);
        setTemplates(list);
        setTemplateKey(list[0]?.key || "");
      })
      .catch(() => toast.error("Erreur de chargement des templates"))
      .finally(() => setLoading(false));
  }, [open, slug]);

  // Aperçu : variables RÉSOLUES côté backend (snapshot devis/dossier).
  const loadPreview = useCallback(async () => {
    if (!devisId || !templateKey) return;
    setPreviewing(true);
    try {
      const res = await fetch(`${API}/api/metier/devis/${encodeURIComponent(devisId)}/email-preview`, {
        method: "POST", headers: headers(slug), credentials: "include",
        body: JSON.stringify({ template_key: templateKey }),
      });
      if (res.ok) {
        const data = await res.json();
        setPreview(data);
        if (!recipient && data.recipient_suggestion) setRecipient(data.recipient_suggestion);
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || `Aperçu impossible (HTTP ${res.status})`);
        setPreview(null);
      }
    } catch {
      toast.error("Erreur réseau (aperçu)");
    } finally {
      setPreviewing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devisId, templateKey, slug]);

  useEffect(() => { if (open && templateKey) loadPreview(); }, [open, templateKey, loadPreview]);

  const handleSend = async () => {
    if (!templateKey) { toast.error("Sélectionnez un template"); return; }
    if (!recipient) { toast.error("Destinataire manquant"); return; }
    if ((preview?.unresolved_variables?.length || 0) > 0) {
      toast.error("Variables non résolues — template inadapté à ce devis.");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/api/metier/devis/${encodeURIComponent(devisId)}/send-email`, {
        method: "POST", headers: headers(slug), credentials: "include",
        body: JSON.stringify({ template_key: templateKey, recipient }),
      });
      const data = await res.json();
      // Statut RÉEL (point 1) : ok=false ou 4xx/5xx → échec explicite, pas de faux "envoyé".
      if (res.ok && data.ok && data.status === "sent") {
        toast.success(`✅ Envoyé à ${data.recipient} (PJ ${data.pdf_filename})`);
        onSent?.(data);
        onClose();
      } else if (res.ok && data.ok && data.status === "skipped_preprod") {
        toast.success(`Envoi simulé (preprod : egress bloqué) → ${data.recipient}. En prod l'email partira réellement.`);
        onSent?.(data);
        onClose();
      } else {
        const reason = data?.detail || data?.error || `HTTP ${res.status}`;
        toast.error(`❌ Échec : ${reason}`);
      }
    } catch (e) {
      toast.error(`❌ Erreur : ${e.message || e}`);
    } finally {
      setSending(false);
    }
  };

  const resolvedEntries = Object.entries(preview?.resolved_variables || {});
  const unresolved = preview?.unresolved_variables || [];
  const blocked = unresolved.length > 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="send-devis-omv-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-[var(--oh-accent-text)]" />
            Envoyer le devis {devis?.numero ? `n° ${devis.numero}` : ""} par email
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {/* Template */}
          <div>
            <Label className="text-xs">Template email ({templates.length})</Label>
            {loading ? (
              <div className="flex items-center gap-2 text-xs text-[var(--oh-text-secondary)]">
                <Loader2 className="w-3 h-3 animate-spin" /> Chargement…
              </div>
            ) : templates.length === 0 ? (
              <p className="text-xs text-[var(--oh-text-secondary)]">
                Aucun template email. Créez-en un dans Design Studio → Emails Métier.
              </p>
            ) : (
              <select
                data-testid="devis-template-select"
                value={templateKey}
                onChange={(e) => { setTemplateKey(e.target.value); setPreview(null); }}
                className="w-full px-2 py-1.5 text-sm rounded"
                style={{ background: "var(--oh-bg-elevated)", color: "var(--oh-text)", border: "1px solid var(--oh-border)" }}
              >
                {templates.map((t) => (
                  <option key={t.key} value={t.key}>{t.name || t.key}</option>
                ))}
              </select>
            )}
          </div>

          {/* Destinataire */}
          <div>
            <Label className="text-xs">Destinataire (email du bénéficiaire)</Label>
            <Input
              type="email"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder="contact@beneficiaire.fr"
              data-testid="devis-email-recipient"
            />
          </div>

          {/* Aperçu variables résolues */}
          {previewing ? (
            <div className="flex items-center gap-2 text-xs text-[var(--oh-text-secondary)]">
              <Loader2 className="w-3 h-3 animate-spin" /> Résolution des variables…
            </div>
          ) : preview ? (
            <div className="space-y-2">
              {blocked && (
                <div
                  className="rounded p-2 text-xs"
                  style={{ background: "var(--oh-stat-red-bg)", border: "1px solid var(--oh-stat-red-border)", color: "var(--oh-stat-red-text)" }}
                  data-testid="devis-email-unresolved"
                >
                  ⚠️ Variables non résolues : <strong>{unresolved.join(", ")}</strong> — ce template est inadapté à ce devis. Envoi bloqué.
                </div>
              )}
              <div className="text-xs">
                <span className="text-[var(--oh-text-secondary)]">Sujet : </span>
                <span style={{ color: "var(--oh-text)" }}>{preview.subject || <em>(vide)</em>}</span>
              </div>
              {resolvedEntries.length > 0 && (
                <div className="text-[11px] grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0.5">
                  {resolvedEntries.map(([k, v]) => (
                    <div key={k} className="truncate">
                      <code className="text-[var(--oh-accent-text)]">{`{{${k}}}`}</code>
                      <span className="text-[var(--oh-text-secondary)]"> → </span>
                      <span style={{ color: "var(--oh-text)" }}>{v === "" ? "—" : String(v)}</span>
                    </div>
                  ))}
                </div>
              )}
              <div
                className="rounded p-3 text-sm max-h-56 overflow-y-auto"
                style={{ background: "white", color: "black" }}
                dangerouslySetInnerHTML={{ __html: preview.html || "<em>(corps vide)</em>" }}
              />
              <p className="text-[11px] text-[var(--oh-text-muted)]">
                Le PDF du devis sera joint automatiquement. Envoi depuis le sender du tenant.
              </p>
            </div>
          ) : null}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={sending}>Annuler</Button>
          <Button
            data-testid="devis-email-send-btn"
            onClick={handleSend}
            disabled={sending || !templateKey || !recipient || blocked}
            title={blocked ? "Variables non résolues — template inadapté" : undefined}
            className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)] text-[var(--oh-accent-contrast)]"
          >
            {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
            {devis?.email_envoi?.status === "sent" ? "Renvoyer" : "Envoyer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
